import React, { forwardRef, KeyboardEvent } from "react";

import { bemClassNames, createQA, isActivatingKeyEvent } from "@multiweb/reactUtils";
import { IconCode } from "@multiweb/types/contracts";
import { ForwardRefType, QAProps } from "@multiweb/types/react";
import { BooleanSwitcher, BooleanSwitcherChildrenProps } from "../BooleanSwitcher";
import { Icon, IconColor } from "../Icon";
import { Layout, SpacingSize } from "../Layout";
import { FontWeight, Text, TextColor } from "../Text";
import { AlignItems } from "../types";

const classNames = bemClassNames("select-item-component");

export type SelectItemProps = {
    /** Text položky */
    text: string;
    /** Funkce volaná při výběru položky */
    onSelect: () => void;
    /** Příznak, zda je položka vybraná */
    isSelected?: boolean;
    /** Příznak, zda je položka zakázána */
    disabled?: boolean;
} & QAProps;

/** Komponenta položky těla rozevíracího seznamu */
export const SelectItem: ForwardRefType<HTMLButtonElement, SelectItemProps> = forwardRef<HTMLButtonElement, SelectItemProps>(({
    qa,
    text,
    onSelect,
    isSelected = false,
    disabled = false,
}, ref) => {
    const handleKeyDown = (event: KeyboardEvent) => {
        if (isActivatingKeyEvent(event)) {
            event.preventDefault();
            onSelect();
        }
    };

    return (
        <BooleanSwitcher>
            {({ value: isHovered, turnOn: onMouseEnter, turnOff: onMouseLeave }: BooleanSwitcherChildrenProps) => (
                <button
                    className={classNames({ disabled, isSelected, hovered: isHovered })}
                    onKeyDown={handleKeyDown}
                    onClick={onSelect}
                    {...createQA(qa)}
                    {...{ disabled, onMouseEnter, onMouseLeave, ref }}
                >
                    <Layout
                        spacingSizeHorizontal={SpacingSize.xs}
                        alignItems={AlignItems.center}
                    >
                        {isSelected ? (
                            <Layout spacingSize={SpacingSize.xs}>
                                <Icon
                                    iconCode={IconCode.uiCheckSmall}
                                    color={isHovered ? IconColor.whiteBlack : IconColor.grey1}
                                    {...{ disabled }}
                                />
                            </Layout>
                        ) : (
                            <Layout spacingSize={SpacingSize.m}/>
                        )}
                        <Layout spacingSize={SpacingSize.xs}>
                            <Text
                                fontWeight={FontWeight.bold}
                                value={text}
                                color={isHovered ? TextColor.whiteBlack : TextColor.blackWhite}
                                isTextSelectable={!disabled}
                                {...{ disabled }}
                            />
                        </Layout>
                    </Layout>
                </button>
            )}
        </BooleanSwitcher>
    );
});
